import { useCallback, useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { useTranslation } from "react-i18next";
import { getOrder, loadOrder, loadOrders } from "@/libs/api/requests";
import { mutationStore } from "@/libs/network/services/OfflineAsyncMutationStore";
import { isAxiosError } from "axios";
import { useTourDetailContext } from "../data/provider";
import { InternalTourStop } from "../types";

export function useTourStopLoader() {
  const { setContext, stops, tour } = useTourDetailContext();

  const { t } = useTranslation();

  const { toast } = useToast();

  const loadTourStop = useCallback(
    (parentId: string, taskId: string, isScan?: boolean) => {
      toast({
        title: t("toasts.orderPickup.success"),
        variant: "success",
      });

      setContext({
        type: "updateLoaded",
        taskId: taskId,
        parentId,
      });

      mutationStore.enqueueMutation({
        mutate: () =>
          loadOrder(parentId).then(() => {
            if (!isScan) {
              return;
            }
            getOrder({ orderId: parentId, isScan });
          }),
        resolve: () => {},
        reject: (error) => {
          // TODO: make this prettier. This is a workaround to update
          // the state of the matching delivery task
          // This should happen inside the reducer
          setContext({
            type: "new",
            newState: {
              loading: false,
              error: null,
              tour: tour,
              stops: stops.map((stop) => {
                return {
                  ...stop,
                  items: stop.items.map((item) => {
                    if (item.id === taskId) {
                      return {
                        ...item,
                        state: "loaded",
                      };
                    }

                    return item;
                  }),
                };
              }),
            },
          });

          setContext({
            type: "updateTourStopState",
            stopId: taskId,
            newState: "loaded",
          });

          if (isAxiosError(error) && error.response) {
            toast({
              title: t("toasts.orderPickup.error"),
              description: error.response.data.error,
              variant: "error",
            });
          }
        },
      });
    },
    [setContext, stops, t, toast, tour],
  );

  const loadAllTourStops = useCallback(
    async (currentStop: InternalTourStop) => {
      if (!currentStop) {
        return;
      }

      const orderIds = currentStop.items
        .filter((item) => item.state !== "done")
        .map((item) => item.parentId);

      if (orderIds.length === 0) {
        return;
      }

      const previousStates = new Map(
        currentStop.items
          .filter((item) => item.state !== "done")
          .map((item) => [item.id, item.state]),
      );

      for (const item of currentStop.items) {
        if (item.state === "done") {
          continue;
        }

        setContext({
          type: "updateLoaded",
          taskId: item.id,
          parentId: item.parentId,
        });
      }

      mutationStore.enqueueMutation({
        mutate: () => loadOrders(orderIds),
        resolve: () => {},
        reject: (error) => {
          Array.from(previousStates.entries()).forEach(
            ([taskId, previousState]) => {
              setContext({
                type: "updateTourStopState",
                stopId: taskId,
                newState: previousState,
              });
            },
          );

          if (isAxiosError(error) && error.response) {
            toast({
              title: t("toasts.orderPickup.error"),
              description: error.response.data.error,
              variant: "error",
            });
          }
        },
      });
    },
    [setContext, t, toast],
  );

  return {
    loadTourStop,
    loadAllTourStops,
  };
}
